/**
 * kDocChartSvgDraw - kDocChartSvg's bar, stack, line, legend and pie drawing members.
 * Split out of kDocChartSvgCore.ts (2026-10-19, owner: smaller files for the reuse reviewer). The bodies are
 * moved VERBATIM except that `this` is the explicit first parameter `Self` (private/protected members are
 * read as Self["Name"]); kDocChartSvg keeps every member as a same-signature delegating member.
 */

import { getCoreUtils } from "./kCoreHandler";
import type { kDocChartSvg, kDocChart } from "./kDocChartSvgCore";
import type { kDocChartFrame } from "./kDocChartSvgMath";
import { kDocChartSvgMath } from "./kDocChartSvgMath";

// 2026-10-22 (owner: no runtime import cycles): this module no longer imports kDocChartSvgCore at runtime - the pure
// statics it called there live in the leaf kDocChartSvgMath, and the `Self: typeof kDocChartSvg` members below are
// same-signature delegates to it.

export class kDocChartSvgDraw {
    /** The indexes of the series drawn as bars. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.BarSeries - its gate, try/catch and handler label "kDocChartSvg.BarSeries" stay verbatim, so error reports are unchanged.
    public static BarSeries = (Self: kDocChartSvg): number[] => {
        if (getCoreUtils().KErrorMode) { return []; }
        try {
            const Out: number[] = [];
            for (let Index: number = 0; Index < Self["_Chart"].Series.length; Index++) {
                if (Self["_Chart"].Kind === "barline" && Self["_Chart"].Series[Index].AsLine === true) { continue; }
                if (Self["_Chart"].Kind === "line") { continue; }
                Out.push(Index);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.BarSeries");
            return [];
        }
    };

    /** One bar of series S at category I, from Base (a stack's running total) up by its value; its value label. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.DrawBar - its gate, try/catch and handler label "kDocChartSvg.DrawBar" stay verbatim, so error reports are unchanged.
    public static DrawBar = (Self: kDocChartSvg, Frame: kDocChartFrame, S: number, I: number, X: number, Width: number, Base: number, Column: number = 0): number => {
        if (getCoreUtils().KErrorMode) { return Base; }
        try {
            const Value: number | null = Self["_Chart"].Series[S].Values[I];
            if (Value === null || Value === undefined || isNaN(Value)) { return Base; }
            const Y1: number = kDocChartSvgMath.YOf(Frame, Base);
            const Y2: number = kDocChartSvgMath.YOf(Frame, Base + Value);
            Self["_Parts"].push("<rect x=\"" + Self.N(X) + "\" y=\"" + Self.N(Math.min(Y1, Y2)) + "\" width=\"" + Self.N(Width)
                + "\" height=\"" + Self.N(Math.abs(Y1 - Y2)) + "\"" + Self.Paint(S) + Self.MarkEnd("rect", S, I, Value));
            if (Self.LabelledValue(S, I) === true && Self["_Chart"].Kind !== "stacked" && Self.GroupLabels() === false) {
                let LabelY: number = Y2 - Self.LabelSize() * 0.4;
                if (Value < 0) { LabelY = Y2 + Self.LabelSize() * 1.1; }
                Self["_Parts"].push(Self.ColumnText(X + Width / 2, LabelY, Self.Label(Value), Math.max(Width, Column)));
            }
            return Base + Value;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.DrawBar");
            return Base;
        }
    };

    /**
     * A category's values are labelled: "all", "last" for the last category, or "extremes" (added 2026-09-29) for
     * the highest, the lowest and the last category - by the stack's total, else the largest value there.
     */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Labelled - its gate, try/catch and handler label "kDocChartSvg.Labelled" stay verbatim, so error reports are unchanged.
    public static Labelled = (Self: kDocChartSvg, Index: number): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (Self["_Chart"].ValueLabels === "all") { return true; }
            const Last: boolean = Index === Self["_Chart"].Categories.length - 1;
            if (Self["_Chart"].ValueLabels === "extremes") { return Last === true || Self.Extremes().indexOf(Index) >= 0; }
            return Self["_Chart"].ValueLabels === "last" && Last === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Labelled");
            return false;
        }
    };

    /** The categories with the highest and the lowest height (the first of equals): [high, low]. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Extremes - its gate, try/catch and handler label "kDocChartSvg.Extremes" stay verbatim, so error reports are unchanged.
    public static Extremes = (Self: kDocChartSvg): number[] => {
        if (getCoreUtils().KErrorMode) { return []; }
        try {
            let High: number = 0;
            let Low: number = 0;
            for (let Index: number = 1; Index < Self["_Chart"].Categories.length; Index++) {
                if (Self.HeightAt(Index) > Self.HeightAt(High)) { High = Index; }
                if (Self.HeightAt(Index) < Self.HeightAt(Low)) { Low = Index; }
            }
            return [High, Low];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Extremes");
            return [];
        }
    };

    /** A category's height: the stack's sum ("stacked"), else its largest value; missing values count as 0. */
    public static HeightAt = (Self: kDocChartSvg, Index: number): number => {
        // DOCUMENTED EXCEPTION: canonical static helper owns its gate and original fault label.
        return kDocChartSvgMath.HeightOf(Self["_Chart"], Index, false, "kDocChartSvg.HeightAt");
    };

    /** Category height without constructing a renderer. LegacyZeroFloor preserves a zero-based
     * summary's max and its stacked NaN propagation; default renderer behavior skips NaN. */
    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgMath.HeightOf (it owns the gate and handler label).
    public static HeightOf = (Self: typeof kDocChartSvg, Chart: kDocChart, Index: number, LegacyZeroFloor: boolean = false,
                              FaultName: string = "kDocChartSvg.HeightOf"): number => {
        return kDocChartSvgMath.HeightOf(Chart, Index, LegacyZeroFloor, FaultName);
    };

    /** Stacked columns: positive values up from 0, negative down; the stack's total as its label. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.DrawStacks - its gate, try/catch and handler label "kDocChartSvg.DrawStacks" stay verbatim, so error reports are unchanged.
    public static DrawStacks = (Self: kDocChartSvg, Frame: kDocChartFrame): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const Gap: number = Self.Or(Self["_Style"].BarGapPct, 30) / 100;
            for (let Index: number = 0; Index < Self["_Chart"].Categories.length; Index++) {
                const Slot: number[] = Self.SlotOf(Frame, Index);
                const X: number = Slot[0] + Slot[1] * Gap / 2;
                const Width: number = Slot[1] * (1 - Gap);
                let Up: number = 0;
                let Down: number = 0;
                for (let S: number = 0; S < Self["_Chart"].Series.length; S++) {
                    const Value: number | null = Self["_Chart"].Series[S].Values[Index];
                    if (Value === null || Value === undefined || isNaN(Value)) { continue; }
                    if (Value >= 0) { Up = Self.DrawBar(Frame, S, Index, X, Width, Up); } else { Down = Self.DrawBar(Frame, S, Index, X, Width, Down); }
                }
                if (Self.Labelled(Index) === true) {
                    Self["_Parts"].push(Self.ColumnText(X + Width / 2, kDocChartSvgMath.YOf(Frame, Up) - Self.LabelSize() * 0.4, Self.Label(Up + Down), Slot[1]));
                }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.DrawStacks");
        }
    };

    /** Line series ("line": every series; "barline": the AsLine ones) through the slot centres, with dots. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.DrawLines - its gate, try/catch and handler label "kDocChartSvg.DrawLines" stay verbatim, so error reports are unchanged.
    public static DrawLines = (Self: kDocChartSvg, Frame: kDocChartFrame): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            for (let S: number = 0; S < Self["_Chart"].Series.length; S++) {
                const Line: boolean = Self["_Chart"].Kind === "line" || (Self["_Chart"].Kind === "barline" && Self["_Chart"].Series[S].AsLine === true);
                if (Line === true) { Self.DrawLine(Frame, S); }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.DrawLines");
        }
    };

    /** One line series: a polyline (broken at missing values), a dot per value, its value labels. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.DrawLine - its gate, try/catch and handler label "kDocChartSvg.DrawLine" stay verbatim, so error reports are unchanged.
    public static DrawLine = (Self: kDocChartSvg, Frame: kDocChartFrame, S: number): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const Color: string = Self.ColorOf(S);
            let Points: string[] = [];
            for (let I: number = 0; I < Self["_Chart"].Categories.length; I++) {
                const Value: number | null = Self["_Chart"].Series[S].Values[I];
                if (Value === null || Value === undefined || isNaN(Value)) { Self.Polyline(Points, Color, S); Points = []; continue; }
                const Slot: number[] = Self.SlotOf(Frame, I);
                const X: number = Slot[0] + Slot[1] / 2;
                const Y: number = kDocChartSvgMath.YOf(Frame, Value);
                Points.push(Self.N(X) + "," + Self.N(Y));
                let Radius: string = "1.6";
                if (Self["_Web"].DotRadius > 0) { Radius = kDocChartSvgMath.Num(Self["_Web"].DotRadius); }
                Self["_Parts"].push("<circle cx=\"" + Self.N(X) + "\" cy=\"" + Self.N(Y) + "\" r=\"" + Radius + "\"" + Self.Paint(S)
                    + Self.MarkEnd("circle", S, I, Value));
                if (Self.Labelled(I) === true) { Self["_Parts"].push(Self.ColumnText(X, Y - Self.LabelSize() * 0.6, Self.Label(Value), Slot[1])); }
            }
            Self.Polyline(Points, Color, S);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.DrawLine");
        }
    };

    /**
     * A polyline through Points (two or more). ⚠ kDocChartWeb ClassColors: series S's class, `fill="none"` - the
     * page's CSS strokes a classed line.
     */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.Polyline - its gate, try/catch and handler label "kDocChartSvg.Polyline" stay verbatim, so error reports are unchanged.
    public static Polyline = (Self: kDocChartSvg, Points: string[], Color: string, S: number = 0): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (Points.length < 2) { return; }
            if (Self["_Web"].ClassColors === true) {
                Self["_Parts"].push("<polyline" + Self.Paint(S) + " points=\"" + Points.join(" ") + "\" fill=\"none\" stroke-width=\"2\"/>");
                return;
            }
            Self["_Parts"].push("<polyline points=\"" + Points.join(" ") + "\" fill=\"none\" stroke=\"" + Color + "\" stroke-width=\"1.25\"/>");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.Polyline");
        }
    };

    /** The legend (2+ series): a swatch and the name per series, in one row at the top. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.DrawLegend - its gate, try/catch and handler label "kDocChartSvg.DrawLegend" stay verbatim, so error reports are unchanged.
    public static DrawLegend = (Self: kDocChartSvg): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            if (Self.LegendHeight() === 0) { return; }
            const Size: number = Self.Size();
            let X: number = 0;
            for (let S: number = 0; S < Self["_Chart"].Series.length; S++) {
                Self["_Parts"].push("<rect x=\"" + Self.N(X) + "\" y=\"" + Self.N(Size * 0.2) + "\" width=\"" + Self.N(Size)
                    + "\" height=\"" + Self.N(Size) + "\"" + Self.Paint(S) + "/>");
                Self["_Parts"].push(Self.Text(X + Size * 1.4, Size * 1.05, Self["_Chart"].Series[S].Name, "start", Size, Self.TextColor()));
                X = X + Size * 2.4 + Self["_Chart"].Series[S].Name.length * Size * 0.55;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.DrawLegend");
        }
    };

    // ---- pie and donut -----------------------------------------------------------------------------------------

    /** The first series' positive values as slices, clockwise from 12 o'clock; the legend on the right. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.DrawPie - its gate, try/catch and handler label "kDocChartSvg.DrawPie" stay verbatim, so error reports are unchanged.
    public static DrawPie = (Self: kDocChartSvg): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const Values: (number | null)[] = Self["_Chart"].Series[0].Values;
            let Total: number = 0;
            for (const Value of Values) { if (Value !== null && Value > 0) { Total = Total + Value; } }
            if (Total <= 0) { return; }
            const Radius: number = Math.min(Self["_H"], Self["_W"] * 0.55) / 2 - 1;
            const Cx: number = Radius + 1;
            const Cy: number = Self["_H"] / 2;
            let Hole: number = 0;
            if (Self["_Chart"].Kind === "donut") { Hole = Radius * Self.Or(Self["_Style"].DonutHolePct, 55) / 100; }
            let Angle: number = 0;
            for (let I: number = 0; I < Values.length; I++) {
                const Value: number | null = Values[I];
                if (Value === null || !(Value > 0)) { continue; }
                const Sweep: number = Value / Total * 2 * Math.PI;
                Self["_Parts"].push(kDocChartSvgMath.Slice(Cx, Cy, Radius, Hole, Angle, Angle + Sweep, Self.ColorOf(I)));
                Angle = Angle + Sweep;
            }
            Self.DrawPieLegend(Cx + Radius + Self.Size() * 1.5);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.DrawPie");
        }
    };

    /** An annulus / pie sector path from angle A0 to A1 (radians, 0 = 12 o'clock, clockwise). */
    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgMath.Slice (it owns the gate and handler label).
    public static Slice = (Self: typeof kDocChartSvg, Cx: number, Cy: number, R: number, Hole: number, A0: number, A1: number, Color: string): string => {
        return kDocChartSvgMath.Slice(Cx, Cy, R, Hole, A0, A1, Color);
    };

    /** "x y" of the point at Radius and angle A (0 = 12 o'clock, clockwise) around (Cx, Cy). */
    // DOCUMENTED EXCEPTION: delegating member - body moved verbatim to kDocChartSvgMath.Point (it owns the gate and handler label).
    public static Point = (Self: typeof kDocChartSvg, Cx: number, Cy: number, Radius: number, A: number): string => {
        return kDocChartSvgMath.Point(Cx, Cy, Radius, A);
    };

    /** The pie's legend: a swatch, the category and its value per slice, one row each. */
    // DOCUMENTED EXCEPTION: moved body of kDocChartSvg.DrawPieLegend - its gate, try/catch and handler label "kDocChartSvg.DrawPieLegend" stay verbatim, so error reports are unchanged.
    public static DrawPieLegend = (Self: kDocChartSvg, X: number): void => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            const Size: number = Self.Size();
            const Values: (number | null)[] = Self["_Chart"].Series[0].Values;
            let Y: number = Math.max(0, Self["_H"] / 2 - Self["_Chart"].Categories.length * Size * 0.9);
            for (let I: number = 0; I < Self["_Chart"].Categories.length; I++) {
                const Value: number | null = Values[I];
                if (Value === null || !(Value > 0)) { continue; }
                Self["_Parts"].push("<rect x=\"" + kDocChartSvgMath.Num(X) + "\" y=\"" + kDocChartSvgMath.Num(Y) + "\" width=\"" + kDocChartSvgMath.Num(Size)
                    + "\" height=\"" + kDocChartSvgMath.Num(Size) + "\" fill=\"" + Self.ColorOf(I) + "\"/>");
                let Text: string = Self["_Chart"].Categories[I];
                if (Self["_Chart"].ValueLabels !== "none") { Text = Text + ", " + Self.Label(Value); }
                Self["_Parts"].push(Self.Text(X + Size * 1.4, Y + Size * 0.85, Text, "start", Size, Self.TextColor()));
                Y = Y + Size * 1.8;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "kDocChartSvg.DrawPieLegend");
        }
    };
}
